import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {ApiSourcedPaginationRequest} from '@webmate-web-core/shared/api/apibase.api';
import {MarketplaceApi} from '@webmate-web-core/shared/api/rest/marketplace.restapi';
import {MarketplaceItemId, MarketplaceTagKey} from '@webmate-web-core/shared/types/ids';
import {
    MarketplaceInteractionRecorded,
    MarketplaceInteractionSubmission,
    MarketplaceItem,
    MarketplaceItemPlacement,
    MarketplacePlacementSlot,
    MarketplaceRequest,
    MarketplaceTagDefinition
} from '@webmate-web-core/shared/types/marketplace';

/**
 * Application-facing Marketplace service. API access is delegated to the REST
 * adapter while view derivations stay at the feature service boundary.
 */
@Injectable({
    providedIn: 'root'
})
export class MarketplaceService {

    constructor(private marketplaceApi: MarketplaceApi) {
    }

    getPublishedItems$(): Observable<MarketplaceItem[]> {
        return this.marketplaceApi.getPublishedItems$();
    }

    getPublishedItem$(itemId: MarketplaceItemId): Observable<MarketplaceItem> {
        return this.marketplaceApi.getPublishedItem$(itemId);
    }

    recordInteraction$(submission: MarketplaceInteractionSubmission): Observable<MarketplaceInteractionRecorded> {
        return this.marketplaceApi.recordInteraction$(submission);
    }

    getRequestsForCurrentUser$(limit: number): ApiSourcedPaginationRequest<MarketplaceRequest> {
        return this.marketplaceApi.getRequestsForCurrentUser$(limit);
    }

    getRequestsForCurrentCustomer$(limit: number): ApiSourcedPaginationRequest<MarketplaceRequest> {
        return this.marketplaceApi.getRequestsForCurrentCustomer$(limit);
    }

    getFeaturedTags(items: MarketplaceItem[]): MarketplaceTagDefinition[] {
        const tagByKey = new Map<MarketplaceTagKey, MarketplaceTagDefinition>();
        items.flatMap(item => item.tags).forEach(tag => tagByKey.set(tag.key, tag));
        return [...tagByKey.values()]
            .filter(tag => tag.featured)
            .sort((left, right) => left.rank - right.rank);
    }

    getPlacedItems(items: MarketplaceItem[], slot: MarketplacePlacementSlot): MarketplaceItem[] {
        return items
            .map(item => ({
                item,
                placement: item.placements.find(candidate => candidate.slot === slot)
            }))
            .filter((entry): entry is {item: MarketplaceItem; placement: MarketplaceItemPlacement} =>
                entry.placement !== undefined)
            .sort((left, right) => left.placement.rank - right.placement.rank)
            .map(entry => entry.item);
    }
}
